<template>
  <a-modal
    v-model="visible"
    :title="title"
    :footer="null"
    :closable="false"
    class="adaptive-modal custom-promocode-modal"
    @cancel="closeModal"
  >
    <div class="promocode-modal-content">
      <div class="d-flex flex-column justify-content-center mb-4">
        <a-form-model :model="form" ref="form">
          <a-form-model-item
            v-if="isCreateMode || isEditMode"
            label="Category name"
            prop="name"
            :colon="false"
            :rules="[{ required: true, message: 'Category name is required', trigger: ['change'] }]"
          >
            <a-input
              v-model="form.name"
              placeholder="Category name"
              size="large"
              :disabled="loading"
            />
          </a-form-model-item>
          <div v-if="isCreateMode || isAddMode" class="d-flex flex-column">
            <FormSubTitle title="Add:" />
            <a-radio-group v-model="typeAddition" :disabled="loading" class="mb-4">
              <a-radio value="manually">Add single Promo Code</a-radio>
              <a-radio value="upload">Upload Promo Codes from CSV</a-radio>
            </a-radio-group>
            <div v-if="isManually" ref="promocode-content">
              <div
                v-for="(_, index) in form.promocodes"
                :key="index"
                class="d-flex flex-column"
                style="gap: 5px;"
              >
                <a-form-model-item
                  :prop="`promocodes.${index}`"
                  :colon="false"
                  :rules="[{ required: true, message: 'The promo code value must not be empty', trigger: ['change'] }]"
                >
                  <div class="d-flex align-items-center" style="gap: 5px;">
                    <div v-if="form.promocodes.length > 1" class="d-flex align-items-center">
                      <span class="font-size-16 d-flex mr-2">{{ index + 1 }}</span>
                    </div>
                    <a-input
                      v-model="form.promocodes[index]"
                      placeholder="Promo code value"
                      size="large"
                      :disabled="loading"
                    />
                    <a-icon
                      v-if="form.promocodes.length > 1"
                      type="delete"
                      class="text-danger ml-2"
                      :class="{'cursor-pointer' : !loading}"
                      @click="removeManuallyPromocode(index)"
                    />
                  </div>
                </a-form-model-item>
              </div>
              <AddButton @action="addManuallyPromocode" />
            </div>
            <div v-if="isUpload" ref="promocode-content">
              <a-form-model-item
                label="File"
                prop="file"
                :colon="false"
                class="w-full"
                :rules="[{ required: true, message: 'Promo codes file is required', trigger: ['change'] }]"
              >
                <a-upload-dragger
                  name="file"
                  action="#"
                  class="upload-area"
                  accept=".csv"
                  :multiple="false"
                  :show-upload-list="false"
                  :disabled="loading || fileLoading"
                  :before-upload="beforeUpload"
                  @change="fileChange"
                >
                  <p class="ant-upload-drag-icon">
                    <a-icon type="inbox" class="font-size-32" />
                  </p>
                  <p class="ant-upload-text">Click or drag .csv file to this area to upload</p>
                  <p class="ant-upload-hint">Maximum file size: 3mb</p>
                </a-upload-dragger>
                <div v-if="uploadedFileName" class="mt-2">
                  Uploaded: <span class="text-success">{{ uploadedFileName }}</span>
                </div>
              </a-form-model-item>
              <div @click="downloadExampleFile" class="d-flex align-items-center cursor-pointer">
                <a-icon type="download" class="mr-2 text-primary font-size-16" />
                <span class="font-size-16 text-primary">Download file example</span>
              </div>
            </div>
          </div>
        </a-form-model>
      </div>
      <a-row class="adaptive-modal__footer">
        <div
          class="d-flex align-items-center "
          :class="{
            'justify-content-end' : !isEditMode,
            'justify-content-between' : isEditMode,
          }"
        >
          <a-popconfirm
            placement="top"
            :disabled="loading"
            title="Are you sure you want to delete this category? All associated promo codes will be lost"
            :get-popup-container="getPopupContainer"
            okText="Delete"
            okType="danger"
            @confirm="deleteCategory"
          >
            <a-button
              v-if="isEditMode"
              type="danger"
              :disabled="loading"
              class="mr-2"
            >
              Delete
            </a-button>
          </a-popconfirm>
          <div class="d-flex align-items-center">
            <a-button @click="closeModal" class="mr-2">
              Cancel
            </a-button>
            <a-button
              type="primary"
              class="ml-2"
              :disabled="loading"
              @click="action"
            >
              {{ actionButtonName }}
            </a-button>
          </div>
        </div>
      </a-row>
    </div>
  </a-modal>
</template>

<script>
import apiClient from '@/services/axios'
import FormSubTitle from '@/components/custom/form/FormSubTitle.vue'
import AddButton from '@/components/custom/form/AddButton.vue'
import { beforeUpload } from '@/composables/useUploadImage.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'create',
    },
    categoryName: {
      type: String,
      default: '',
    },
    actionButtonName: {
      type: String,
      default: 'Create',
    },
  },
  components: {
    FormSubTitle,
    AddButton,
  },
  data: () => ({
    form: {
      name: '',
      promocodes: [''],
      file: null,
    },
    uploadedFileName: '',
    fileLoading: false,
    typeAddition: 'manually',
    visible: false,
    loading: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (!val) {
          this.visible = false
          return
        }
        this.visible = true
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    isManually() {
      return this.typeAddition === 'manually'
    },
    isUpload() {
      return this.typeAddition === 'upload'
    },
    isCreateMode() {
      return this.mode === 'create'
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    isAddMode() {
      return this.mode === 'add'
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    addManuallyPromocode() {
      this.form.promocodes.push('')

      this.$nextTick(() => {
        const container = document.querySelector('.ant-modal-content')
        if (!container) {
          return
        }

        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        })
      })
    },
    removeManuallyPromocode(index) {
      if (this.loading) {
        return
      }
      this.form.promocodes.splice(index, 1)
    },
    beforeUpload(file) {
      beforeUpload(this, file, 3, null, ['text/csv'])
    },
    fileChange(info) {
      if (info.file.status === 'uploading') {
        this.fileLoading = true
        return
      }

      info.file.status = 'done'

      this.form.file = info.file.originFileObj
      this.uploadedFileName = info.file.name
      this.fileLoading = false
    },
    downloadExampleFile() {
      const fileUrl = '/resources/examples/wheel-promocodes.csv'
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = 'wheel-promocodes-example.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    getFormData() {
      if (this.isUpload) {
        const formData = new FormData()
        if (this.isCreateMode) {
          formData.append('name', this.form.name)
        }

        formData.append('file', this.form.file)
        return formData
      }

      const data = {
        promocodes: this.form.promocodes,
      }

      if (this.isCreateMode) {
        data.name = this.form.name
      }

      return data
    },
    async action() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return
        }

        try {
          this.loading = true

          if (this.isCreateMode) {
            await this.createAction()
            return
          }
          if (this.isEditMode) {
            await this.editAction()
            return
          }
          await this.addAction()
        } finally {
          this.loading = false
        }
      })
    },
    async createAction() {
      const data = this.getFormData()

      const url = `${wheelApiUrl}/admin/promocodes/categories`
      const response = await apiClient.post(url, data)

      if (!response?.data?.status) {
        getErrorMessage(response?.data)
        return
      }

      const categoryId = response?.data?.data?.id
      if (!categoryId) {
        return
      }

      this.$notification.success({
        message: 'Successfully',
        description: `Category "${this.form.name}" successfully created`,
      })
      this.$router.push(`/fortune-wheel/promo-codes/${categoryId}`)
    },
    async editAction() {
      const data = {
        name: this.form.name,
      }

      const categoryId = this.$route?.params?.id

      const url = `${wheelApiUrl}/admin/promocodes/categories/${categoryId}`
      const response = await apiClient.put(url, data)

      if (!response?.data?.status) {
        getErrorMessage(response?.data)
        return
      }

      this.$notification.success({
        message: 'Successfully',
        description: 'Category name successfully changed',
      })
      this.$emit('edit', this.form.name)
      this.closeModal()
    },
    async addAction() {
      const data = this.getFormData()

      const categoryId = this.$route?.params?.id

      const url = `${wheelApiUrl}/admin/promocodes/categories/${categoryId}/promocodes-batch`
      const response = await apiClient.post(url, data)

      if (!response?.data?.status) {
        getErrorMessage(response?.data)
        return
      }

      this.$notification.success({
        message: 'Successfully',
        description: 'Promo codes have been successfully added',
      })
      this.$emit('add')
      this.closeModal()
    },
    async deleteCategory() {
      try {
        this.loading = true

        const categoryId = this.$route?.params?.id

        const url = `${wheelApiUrl}/admin/promocodes/categories/${categoryId}`
        const response = await apiClient.delete(url)

        if (!response?.data?.status) {
          getErrorMessage(response?.data)
          return
        }

        this.$notification.success({
          message: 'Successfully',
          description: 'The category was successfully deleted',
        })
        this.closeModal()
        this.$router.push('/fortune-wheel/promo-codes')
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
  },
  mounted() {
    if (!this.categoryName) {
      return
    }
    this.form.name = this.categoryName
  },
}
</script>

<style lang="scss">
.custom-promocode-modal {
  .ant-modal-content {
    max-height: 620px;
  }
}
.promocode-modal-content {
  display: flex;
  flex-direction: column;
  .ant-radio-inner {
    border: 1px dashed #1890ff;
  }
  .ant-radio-checked .ant-radio-inner {
    border-style: solid;
  }
}
</style>
